import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { ContentCardModule } from '@app/library/content-card/content-card.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { ExercisesFilterComponent } from '@app/library/panes/exercises-drag-scroll/exercises-filter/exercises-filter.component';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { ScreenService } from '@core/services';
import { IconComponent } from '@shared/icon/icon.component';
import { Content, ContentTypeEnum } from 'lingo2-models';
import { takeUntil } from 'rxjs/operators';
import { AbstractContentPane } from '../models/abstract-content-pane';

@Component({
  selector: 'app-exercises-drag-scroll',
  templateUrl: './exercises-drag-scroll.component.html',
  styleUrls: ['./exercises-drag-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,
    LibraryElementsModule,
    ScrollingModule,
    ModalModule,
    MobileModalModule,

    ContentCardModule,
    IconComponent,
    ExercisesFilterComponent,
  ],
})
export class ExercisesDragScrollComponent extends AbstractContentPane implements OnInit {
  protected router = inject(Router);
  protected location = inject(Location);
  private screenService = inject(ScreenService);

  @ViewChild('scroller', { read: CdkVirtualScrollViewport })
  scroller: CdkVirtualScrollViewport;

  public filters: string[];
  public selectedFilter = '0';
  public isDragScrollEnabled = !(this.deviceService.isMobile() || this.deviceService.isTablet());
  public itemSize = 207;
  public reachedPrev = false;
  public reachedNext = false;
  public showModal = false;
  public openedCardIndex: number = null;
  public item: Content;
  public isSmallScreen = true;

  private originalUrl: string;

  ngOnInit() {
    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (width) => {
        this.isSmallScreen = this.screenService.isSmallScreen;
      },
      error: (error) => {
        this.isSmallScreen = false;
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  protected onContentLoaded(viaQuery?: boolean): void {
    this.filterInit();
    this.isVisible = !!this.content.length;
  }

  private filterInit() {
    const contentKeys = Object.keys(ContentTypeEnum);
    this.filters = contentKeys
      .slice(0, contentKeys.length / 2)
      .filter((filterItem) => !!this.content.find((contentItem) => Number(contentItem.type) === Number(filterItem)));
  }

  public selectFilter(filter: string) {
    this.selectedFilter = filter;
    this.markForCheck();
  }

  public filteredContent(): Content[] {
    if (!this.selectedFilter || this.selectedFilter === '0') {
      return this.content;
    }
    return this.content.filter((contentItem) => Number(contentItem.type) === Number(this.selectedFilter));
  }

  moveLeft() {
    this.scroller.scrollToIndex(0, 'smooth');
  }

  moveRight() {
    const offset = this.scroller.measureScrollOffset('left');
    // сдвинуть на 1 карточку
    this.scroller.scrollToOffset(offset + +this.itemSize, 'smooth');
  }

  public togglePrev(event?) {
    if (this.openedCardIndex - 1 >= 0) {
      this.onOpenCard(this.openedCardIndex - 1);
    }
    this.checkReached();
  }

  public toggleNext(event?) {
    if (this.openedCardIndex + 1 <= this.filteredContent().length - 1) {
      this.onOpenCard(this.openedCardIndex + 1);
    }
    this.checkReached();
  }

  public onOpenCard(i) {
    // logger.log('ContentDragScrollComponent:onOpenCard', i);
    this.openedCardIndex = i;
    this.item = this.filteredContent()[i];
    this.checkReached();
    this.showModal = !!this.item;

    const url = this.contentRoute(this.item);
    const currentUrl = this.location.path(false);
    if (currentUrl !== url) {
      this.originalUrl = currentUrl;
      this.location.replaceState(url);

      // отправка событий в аналитику для имитации навигации по страницам
      this.analytics.pageView(url, currentUrl);
    }
  }

  public onCloseCard() {
    // logger.log('ContentDragScrollComponent:onCloseCard');
    this.openedCardIndex = null;
    this.item = null;
    this.showModal = false;

    if (this.originalUrl) {
      this.location.replaceState(this.originalUrl);
    }
  }

  public onCloseModal(event) {
    if (event) {
      this.onCloseCard();
    }
  }

  public checkReached() {
    const reachedPrev = this.openedCardIndex <= 0;
    const reachedNext = this.openedCardIndex >= this.filteredContent().length - 1;

    if (!reachedPrev && !reachedNext) {
      this.reachedPrev = this.reachedNext = false;
    } else if (reachedPrev && reachedNext) {
      this.reachedPrev = this.reachedNext = true;
    } else if (reachedPrev) {
      this.reachedPrev = true;
    } else if (reachedNext) {
      this.reachedNext = true;
    }
  }

  public trackContentByFn(index, item: Content): string {
    return item.id;
  }
}
